import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  Card,
  Icon,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { VisitStatus } from '@taleemabad/db';

interface School {
  id: number;
  name: string;
  emis?: number;
}

export interface Schedule {
  visitPlanId: string;
  status: VisitStatus;
  emis: string;
  schoolName: string;
  coachName: string;
  school: School;
  fromDate: string;
  toDate: string;
  uniqueId: string;
}

export interface RmScheduleSchoolsProps {
  dateRange?: string;
  schedules?: Schedule[];
  onCreate: () => void;
  onEdit: (visitPlanId: string) => void;
  onScheduleClick: (schedule: Schedule) => void;
}

export const RmScheduleSchools = ({
  dateRange,
  schedules = [],
  onScheduleClick,
  onCreate,
  onEdit,
}: RmScheduleSchoolsProps) => (
  <StyledDiv
    flexDirection="column"
    width="100%"
    padding="0 0 var(--rk-spacing-xxxxxl) 0"
  >
    {schedules.length !== 0 && (
      <>
        <Typography variant="textTitleMedium" textAlign="center">
          Observation
        </Typography>
        <Spacing marginBottom="xl" />
        {dateRange && (
          <Typography variant="textTitleMedium" textAlign="center">
            {`Duration: ${dateRange}`}
          </Typography>
        )}

        <Spacing marginBottom="xl" />

        <StyledDiv
          justifyContent="flex-end"
          width="100%"
          gap={8}
          cursor="pointer"
          onClick={() => onEdit(schedules[0]?.visitPlanId)}
        >
          <Icon name="edit" />
          <Typography variant="textTitleMediumPrimary">
            Edit Schedule Dates
          </Typography>
        </StyledDiv>
      </>
    )}

    <Spacing marginBottom="xl" />

    {schedules.length === 0 ? (
      <StyledDiv width="100%" flexDirection="column" alignItem="center">
        <Typography variant="textBodyMediumOnSurfaceVariant" textAlign="center">
          No schedule created yet.
        </Typography>
        <Spacing marginBottom="xxxxxxxl" />
        <StyledDiv
          width="100%"
          flexDirection="column"
          alignItem="center"
          justifyContent="center"
          gap={16}
        >
          <StyledCircleIconWrapper>
            <Icon name="add" size="large" onClick={onCreate} />
          </StyledCircleIconWrapper>
          <Typography variant="textTitleLargePrimary40" textAlign="center">
            Create Schedule
          </Typography>
        </StyledDiv>
      </StyledDiv>
    ) : (
      <>
        <Typography variant="textTitleMedium">Schools List</Typography>
        <Spacing marginBottom="xl" />

        <StyledCardsContainer>
          {schedules.map((schedule) => (
            <Card
              width="100%"
              variant="outline"
              minHeight="0"
              key={schedule.uniqueId}
              onClick={() => onScheduleClick(schedule)}
            >
              {/* TODO: may need it later */}

              {/* <StyledCardHeader>
                <Typography
                  variant={
                    schedule.status === VisitStatus.Completed
                      ? 'textLabelLargeSuccess'
                      : 'textBodyMediumError'
                  }
                >
                  {schedule.status === VisitStatus.Completed
                    ? 'Complete'
                    : 'Incomplete'}
                </Typography>
                <Icon name="chevron_right" />
              </StyledCardHeader> */}

              <StyledCardHeader>
                <StyledTextWrapper>
                  <Typography variant="textTitleMedium">
                    {schedule.emis} – {schedule.schoolName}
                  </Typography>
                </StyledTextWrapper>
                <Icon name="chevron_right" />
              </StyledCardHeader>
              <Typography variant="textBodyLarge">
                {schedule.coachName.split(',').length === 1
                  ? 'Coach Assigned:'
                  : 'Coaches Assigned:'}
              </Typography>
              <Spacing marginTop="sm" />
              <StyledDiv flexDirection="column" gap={2}>
                {schedule.coachName.split(',').map((coach, index) => (
                  <Typography key={index} variant="textBodyMedium">
                    • {coach.trim()}
                  </Typography>
                ))}
              </StyledDiv>
            </Card>
          ))}

          <Spacing marginBottom="xl" />
        </StyledCardsContainer>
      </>
    )}
  </StyledDiv>
);

const StyledTextWrapper = styled.div`
  width: 100%;
  word-break: break-word;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const StyledCardsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--rk-spacing-xl);

  ${mq(BreakpointSizes.desktop)} {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

const StyledCircleIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid var(--md-sys-color-outline);
  cursor: pointer;
`;

const StyledCardHeader = styled(StyledDiv)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;
